import { useEffect, useState, useRef } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

function MyDocuments() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFileName, setSelectedFileName] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Aadhar Card',
    file: null
  });

  useEffect(() => {
    fetchMyDocuments();
  }, []);

  useEffect(() => {
    filterDocuments();
  }, [documents, statusFilter, searchTerm]);

  const fetchMyDocuments = async () => {
    try {
      const response = await api.get('/documents/my-documents');
      setDocuments(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = [...documents];

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(doc => doc.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(doc =>
        doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredDocuments(filtered);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!formData.file) {
      alert('Please select a file to upload');
      return;
    }

    const uploadData = new FormData();
    uploadData.append('title', formData.title);
    uploadData.append('description', formData.description);
    uploadData.append('category', formData.category);
    uploadData.append('document', formData.file);

    try {
      const response = await api.post('/documents', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setShowUploadModal(false);
      setFormData({
        title: '',
        description: '',
        category: 'Aadhar Card',
        file: null
      });
      setSelectedFileName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchMyDocuments();
      alert('Document uploaded successfully!');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error uploading document';
      alert(errorMsg);
      console.error('Upload error:', error);
    }
  };

  const handleDownload = async (id, filename) => {
    try {
      const response = await api.get(`/documents/${id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error downloading document');
      console.error('Download error:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      approved: 'badge-success',
      rejected: 'badge-danger'
    };
    return badges[status] || 'badge-info';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Aadhar Card': '🆔',
      'PAN Card': '💳',
      'Bank Account': '🏦',
      'Passport': '🛂',
      'Driving License': '🚗',
      'Marksheet': '📜',
      'Degree Certificate': '🎓',
      'Experience Letter': '📋',
      'Offer Letter': '💼',
      'Appointment Letter': '📑',
      'Relieving Letter': '📄',
      'Policy': '📋',
      'Contract': '📝',
      'Report': '📊',
      'Other': '📄'
    };
    return icons[category] || '📄';
  };

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  return (
    <div className="container">
      <div className="flex-between mb-20" style={{ flexWrap: 'wrap', gap: '10px' }}>
        <h1 style={{ fontSize: 'clamp(20px, 5vw, 28px)', margin: 0 }}>📁 My Documents</h1>
        <button className="btn btn-primary" onClick={() => setShowUploadModal(true)} style={{ whiteSpace: 'nowrap' }}>
          Upload Document
        </button>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Search Documents
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by title, description, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Filter by Status
            </label>
            <select
              className="form-control"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Documents</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Documents Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', 
        gap: '20px' 
      }}>
        {filteredDocuments.length === 0 ? (
          <div className="card" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#666', fontSize: '16px' }}>No documents found</p>
            <p style={{ color: '#999', fontSize: '14px', marginTop: '10px' }}>
              Upload your first document to get started
            </p>
          </div>
        ) : (
          filteredDocuments.map((doc) => (
            <div key={doc._id} className="card" style={{ 
              display: 'flex', 
              flexDirection: 'column',
              height: '100%'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                marginBottom: '15px',
                paddingBottom: '15px',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <span style={{ fontSize: '32px' }}>{getCategoryIcon(doc.category)}</span>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600' }}>
                    {doc.title}
                  </h3>
                  <span className={`badge ${getStatusBadge(doc.status)}`}>
                    {doc.status}
                  </span>
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <p style={{ 
                  fontSize: '13px', 
                  color: '#666',
                  marginBottom: '10px',
                  lineHeight: '1.5'
                }}>
                  {doc.description || 'No description provided'}
                </p>

                <div style={{ fontSize: '12px', color: '#999', marginBottom: '15px' }}>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Category:</strong> {doc.category}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Uploaded:</strong> {new Date(doc.createdAt).toLocaleDateString()}
                  </p>
                  {doc.status === 'approved' && doc.approvedAt && (
                    <p style={{ margin: '5px 0', color: '#10b981' }}>
                      <strong>Approved:</strong> {new Date(doc.approvedAt).toLocaleDateString()}
                    </p>
                  )}
                  {doc.rejectionReason && (
                    <p style={{ margin: '5px 0', color: '#dc2626' }}>
                      <strong>Rejection Reason:</strong> {doc.rejectionReason}
                    </p>
                  )}
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                paddingTop: '15px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => handleDownload(doc._id, doc.filename)}
                  style={{ flex: 1, fontSize: '12px', padding: '8px' }}
                >
                  📥 Download
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Upload Document</h2>
              <button className="close-btn" onClick={() => setShowUploadModal(false)}>
                &times;
              </button>
            </div>

            <form onSubmit={handleUpload}>
              <div className="form-group">
                <label>Document Title *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Enter document title"
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  className="form-control"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <optgroup label="Personal Documents">
                    <option value="Aadhar Card">Aadhar Card</option>
                    <option value="PAN Card">PAN Card</option>
                    <option value="Bank Account">Bank Account Details</option>
                    <option value="Passport">Passport</option>
                    <option value="Driving License">Driving License</option>
                  </optgroup>
                  <optgroup label="Educational Documents">
                    <option value="Marksheet">Marksheet</option>
                    <option value="Degree Certificate">Degree Certificate</option>
                  </optgroup>
                  <optgroup label="Professional Documents">
                    <option value="Experience Letter">Experience Letter</option>
                    <option value="Offer Letter">Offer Letter</option>
                    <option value="Appointment Letter">Appointment Letter</option>
                    <option value="Relieving Letter">Relieving Letter</option>
                  </optgroup>
                  <optgroup label="Company Documents">
                    <option value="Policy">Policy</option>
                    <option value="Contract">Contract</option>
                    <option value="Report">Report</option>
                  </optgroup>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-control"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  placeholder="Brief description of the document"
                />
              </div>

              <div className="form-group">
                <label>Upload File *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="form-control"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    setFormData({ ...formData, file });
                    setSelectedFileName(file ? file.name : '');
                  }}
                  required
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                {selectedFileName && (
                  <small style={{ color: '#10b981', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                    ✓ Selected: {selectedFileName}
                  </small>
                )}
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '3px' }}>
                  Accepted formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (Max: 10MB)
                </small>
              </div>

              <div className="flex-end">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowUploadModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Upload Document
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyDocuments;
